"use client";

import type * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils-client";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

/**
 * Checks if an error is a blocking error (not just a warning)
 */
function hasBlockingError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const errorWithIssues = error as {
    issues?: Array<{ severity?: string; message: string }>;
  };

  // If there's no issues array, it's a regular error
  if (!errorWithIssues.issues || !Array.isArray(errorWithIssues.issues)) {
    return !!error;
  }

  // Check if there are any non-warning issues
  return errorWithIssues.issues.some((issue) => issue.severity !== "warning");
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
    // Add a flag for whether this field has blocking errors (not just warnings)
    hasError: hasBlockingError(fieldState.error),
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn("grid gap-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

function FormLabel({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { hasError, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={hasError}
      className={cn("data-[error=true]:text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();

  // Filter out warnings - they should only appear in FormRecommendations
  // Only show actual errors (not warnings) in FormMessage
  if (!error || typeof error !== "object") {
    return props.children ? (
      <p
        data-slot="form-message"
        id={formMessageId}
        className={cn("text-destructive text-sm", className)}
        {...props}
      >
        {props.children}
      </p>
    ) : null;
  }

  // Check if this error has issues (from zodResolverWithWarnings)
  const errorWithIssues = error as {
    message?: string;
    issues?: Array<{ severity?: string; message: string }>;
  };

  // If there are issues, filter out warnings and get first error message
  if (errorWithIssues.issues && Array.isArray(errorWithIssues.issues)) {
    const errorIssues = errorWithIssues.issues.filter(
      (issue) => issue.severity !== "warning",
    );

    // If no actual errors (only warnings), don't show anything
    if (errorIssues.length === 0) {
      return null;
    }

    // Show the first error message
    const body = errorIssues[0]?.message ?? "";
    if (!body) return null;

    return (
      <p
        data-slot="form-message"
        id={formMessageId}
        className={cn("text-destructive text-sm", className)}
        {...props}
      >
        {body}
      </p>
    );
  }

  // Fallback to regular error message if no issues array
  const body = error.message ?? props.children ?? "";
  if (!body) return null;

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn("text-destructive text-sm", className)}
      {...props}
    >
      {body}
    </p>
  );
}

/**
 * Displays Zod v4 warnings (recommendations) separately from errors.
 *
 * Warnings are validation issues with severity="warning" that provide
 * helpful suggestions to improve data quality without blocking form submission.
 *
 * Works with zodResolverWithWarnings which preserves the issues array
 * with severity information in the error object.
 *
 * Example usage:
 * <FormField
 *   control={form.control}
 *   name="title"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Title</FormLabel>
 *       <FormControl>
 *         <Input {...field} />
 *       </FormControl>
 *       <FormMessage />
 *       <FormRecommendations />
 *     </FormItem>
 *   )}
 * />
 */
function FormRecommendations({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { error } = useFormField();

  // Early return if no error
  if (!error || typeof error !== "object") return null;

  // Check if error has issues array (preserved by zodResolverWithWarnings)
  const errorWithIssues = error as {
    issues?: Array<{ severity?: string; message: string }>;
  };

  // Filter for warnings only (severity === "warning")
  const warnings =
    errorWithIssues.issues?.filter(
      (issue) => issue.severity === "warning",
    ) || [];

  if (warnings.length === 0) return null;

  return (
    <div
      data-slot="form-recommendations"
      className={cn("mt-2 space-y-1", className)}
      {...props}
    >
      {warnings.map((warning, idx) => (
        <div
          key={idx}
          className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-2 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200"
        >
          <span className="mt-0.5 flex-shrink-0" aria-label="Recommendation">
            💡
          </span>
          <span className="flex-1">{warning.message}</span>
        </div>
      ))}
    </div>
  );
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormRecommendations,
  FormField,
};
